import {sendEvent} from '@github-ui/hydro-analytics'

import type {CopilotChatMessage} from './copilot-chat-types'

/**
 * Constructs message hierarchy, populating parentMessage & childMessages using parentMessageID reference in each message.
 * @param messages complete array of messages
 */
export function constructMessagesHierarchy(messages: CopilotChatMessage[], selectMostRecentMessages: boolean = true) {
  const startTime = performance.now()
  let timeElapsed = 0

  const rootMessage: CopilotChatMessage = {
    id: 'root',
    role: 'user',
    createdAt: '',
    threadID: '',
    references: null,
    childMessages: [],
    messageIndex: 0,
    clientSide: false,
  }

  // The first message does not have a parent. In order to keep track of possible edits to the first prompt,
  // we add an invisible root message that all first messages are linked to. This is pushed to the front of the array.
  if (messages.length === 0) {
    messages.push(rootMessage)

    timeElapsed = performance.now() - startTime
    sendEvent('copilot.timings', {
      function: 'constructMessagesHierarchy',
      timeElapsed,
      messagesLength: messages.length,
      selectMostRecentMessages,
    })
    return
  }

  // Handle messages coming from the old structure that did not have a root node yet.
  if (messages[0]!.id !== 'root') {
    messages.unshift(rootMessage)

    sendEvent('copilot.legacy_thread_updated', {
      threadID: messages[1]!.threadID,
      messagesLength: messages.length,
      selectMostRecentMessages,
    })
  }

  // Virtual "termination" parent message ID that gets sent to server if first message is edited, to disambiguate
  // all potential first messages. Point the first message to the root message.
  // See service.go
  let previousMessage: CopilotChatMessage = messages[1]!
  previousMessage.parentMessageID = 'root'

  const messageIdMap = new Map<string, CopilotChatMessage>()
  for (const message of messages) {
    messageIdMap.set(message.id, message)
  }

  let messageIndex = 0
  for (const message of messages) {
    message.messageIndex = messageIndex
    messageIndex++

    // Handle messages with no parent, which come from the old flat structure.
    // We iterate from oldest to most recent, linking each message to the previous one as its parent.
    if (!message.parentMessageID && message.id !== 'root') {
      message.parentMessageID = previousMessage.id
      previousMessage = message
    }

    // By the time we get here, each message should have a parent. We can now link the parents to their children
    // by adding them to the list of childMessages on the parent.
    message.siblingIndex = -1
    message.childMessages = []
    const parentMessage = messageIdMap.get(message.parentMessageID!)
    if (parentMessage) {
      message.parentMessage = parentMessage
      parentMessage.childMessages!.push(message)
      message.siblingIndex = parentMessage.childMessages!.length - 1
      // In the event of reconstruction, we want to preserve the selected child index.
      if (parentMessage.selectedChildIndex === undefined) {
        parentMessage.selectedChildIndex = message.siblingIndex
      }
    }
  }

  // We want the selected subthreads to show the most recent message by default.
  // Start with the most recent message and work backward to determine which
  // messages to select.
  if (selectMostRecentMessages) {
    let selectedNode = messages.at(-1)
    while (selectedNode) {
      const parent = selectedNode.parentMessage
      if (parent) {
        parent.selectedChildIndex = selectedNode.siblingIndex
      }

      selectedNode = parent
    }
  }

  timeElapsed = performance.now() - startTime
  sendEvent('copilot.timings', {
    function: 'constructMessagesHierarchy',
    timeElapsed,
    messagesLength: messages.length,
    selectMostRecentMessages,
  })
}

export function getActiveMessages(messages: CopilotChatMessage[]): CopilotChatMessage[] {
  let currentNode = messages[0]
  const activeMessages: CopilotChatMessage[] = []
  while (currentNode) {
    if (currentNode.id !== 'root') {
      activeMessages.push(currentNode)
    }

    if (currentNode.selectedChildIndex !== undefined) {
      currentNode = currentNode.childMessages![currentNode.selectedChildIndex]
    } else {
      break
    }
  }

  return activeMessages
}

export function selectActiveMessages(messages: CopilotChatMessage[], selectedMessage: CopilotChatMessage) {
  const messageToSelect = messages.find(message => message.id === selectedMessage.id)
  if (messageToSelect && messageToSelect.parentMessage) {
    messageToSelect.parentMessage.selectedChildIndex = messageToSelect.siblingIndex
  }
}

export function unselectPreviousChild(messages: CopilotChatMessage[], parentMessage: CopilotChatMessage) {
  const messageToUpdate = messages.find(message => message.id === parentMessage.id)
  if (messageToUpdate && messageToUpdate.selectedChildIndex !== undefined) {
    messageToUpdate.selectedChildIndex = undefined
  }
}
